import { datadogRum } from '@datadog/browser-rum';
import { isLocalEnvironment } from '~/constants/environment';
import BrowserCookies from './BrowserCookies';
import toError from './toError';

type Context = Record<string, any>;

/** Informational logging */

function logInfoToConsole(message: string, context: Context) {
  console.log({ message, context });
}

function logInfoToTelemetry(message: string, context: Context) {
  datadogRum.addAction(message, context);
}

export function logInfo(message: string, context: Context = {}) {
  if (isLocalEnvironment) {
    logInfoToConsole(message, context);
  } else {
    logInfoToTelemetry(message, context);
  }
}

/** Error logging */

type ErrorWithResponse = {
  response?: {
    data?: string;
  };
};

export type LogErrorOptions = {
  callback?: (_: Error) => void;
  controller?: AbortController;
};

function logErrorToConsole(error: Error, context?: Context) {
  console.error({
    message: error.message,
    stack: error.stack,
    context
  });
}

function logErrorToTelemetry(error: Error, context?: Context) {
  datadogRum.addError(error, context);
}

export function isErrorWithResponse(err: unknown): err is ErrorWithResponse {
  return typeof err === 'object' && err !== null && 'response' in err;
}

export function logError(
  err: unknown,
  context: Context = {},
  options: LogErrorOptions = {}
) {
  const error = toError(err);
  const { callback, controller } = options;

  // Log aborts as info instead of errors
  if (controller?.signal?.aborted) {
    logInfo('Request aborted', {
      ...context,
      reason: controller.signal.reason
    });

    return;
  }

  if (isLocalEnvironment) {
    logErrorToConsole(error, { err, ...context });
  } else {
    logErrorToTelemetry(error, { err, ...context });
  }

  if (callback) callback(error);
}

export const generateErrorLogger =
  (location: string, globalContext?: Context, options?: LogErrorOptions) =>
  (err: unknown, instanceContext?: Context) =>
    logError(
      err,
      {
        location,
        ...globalContext,
        ...instanceContext,
        BrowserCookieId: BrowserCookies.get('BrowserCookieId') || '',
        fullStoryId: BrowserCookies.get('fs_uid') || '',
        url: window.location.href
      },
      options
    );
